import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

/**
 * Response from POST /workflow/{orgid}/{workflowid}/{session_id}/pause-workflow.
 *
 * The endpoint is fire-and-acknowledge: the backend signals the
 * Temporal workflow and returns immediately.  The pause itself
 * happens asynchronously when the workflow's signal handler runs,
 * which delivers a ``lifecycle/paused`` event over the WebSocket
 * channel; the FE flips the status bar on that event.
 */
export interface PauseWorkflowResponse {
  success: boolean;
  message: string;
  session_id: string;
  /**
   * True when the backend discovered the workflow was already
   * terminal on Temporal (worker died long ago, retention dropped,
   * etc.) and reconciled the PG row to a terminal status.  The
   * caller should treat this as a graceful no-op: no error toast,
   * tear down the live-run UI so the user stops trying to interact
   * with a dead run.  Not present on the normal pause-accepted path.
   */
  was_already_completed?: boolean;
  /** Present only on error envelopes from apiRequest. */
  detail?: string;
}

/**
 * Request the backend pause a running workflow.
 *
 * The endpoint signals AgenticWorkflow.pause() on the Temporal
 * handle keyed by session_id.  In-flight node activities are
 * cancelled abruptly; partial results that already completed stay
 * recorded in node_result rows.  The workflow parks on a durable
 * Temporal wait_condition until a matching resume signal arrives,
 * so a user who pauses today can resume tomorrow without losing
 * any work.
 *
 * Only available when the backend has USE_TEMPORAL=true; the legacy
 * celery executor returns 400.
 */
export const pauseWorkflow = async (
  workflowId: string,
  sessionId: string,
  orgId: string,
): Promise<PauseWorkflowResponse> => {
  try {
    const response = await apiRequest<PauseWorkflowResponse>({
      url: `/workflow/${orgId}/${workflowId}/${sessionId}/pause-workflow`,
      method: 'POST',
      target: 'workflow',
      body: {},
    });
    return response as PauseWorkflowResponse;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'pause-workflow', workflowId, sessionId },
    });
    throw error;
  }
};
